import { useState, useEffect, useRef } from "react";
import { Bug, Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { posthog } from "@/lib/analytics";
import { useAuth } from "@/hooks/use-auth";
import { useLocationStore } from "@/lib/store";

const POSTHOG_PROJECT_ID = (import.meta.env.VITE_POSTHOG_PROJECT_ID as string | undefined) || "";
const POSTHOG_HOST = "https://us.posthog.com";

const errorBuffer: { ts: number; message: string }[] = [];
let bufferInstalled = false;

function installConsoleBuffer() {
  if (bufferInstalled || typeof window === "undefined") return;
  bufferInstalled = true;
  const orig = console.error;
  console.error = (...args: any[]) => {
    try {
      const message = args.map(a => {
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ""}`;
        if (typeof a === "object") { try { return JSON.stringify(a); } catch { return String(a); } }
        return String(a);
      }).join(" ");
      errorBuffer.push({ ts: Date.now(), message });
      if (errorBuffer.length > 50) errorBuffer.shift();
    } catch {}
    orig.apply(console, args);
  };
  window.addEventListener("error", (e) => {
    errorBuffer.push({ ts: Date.now(), message: `${e.message} @ ${e.filename}:${e.lineno}:${e.colno}` });
    if (errorBuffer.length > 50) errorBuffer.shift();
  });
  window.addEventListener("unhandledrejection", (e) => {
    errorBuffer.push({ ts: Date.now(), message: `Unhandled rejection: ${String(e.reason)}` });
    if (errorBuffer.length > 50) errorBuffer.shift();
  });
}

export function BugReporter() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [location] = useLocation();
  const { selectedLocationId } = useLocationStore();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const fired = useRef(false);

  useEffect(() => { installConsoleBuffer(); }, []);

  const submit = useMutation({
    mutationFn: async () => {
      const sessionId = posthog.get_session_id?.() || null;
      const distinctId = posthog.get_distinct_id?.() || null;
      const replayUrl = sessionId && POSTHOG_PROJECT_ID
        ? `${POSTHOG_HOST}/project/${POSTHOG_PROJECT_ID}/replay/${sessionId}`
        : sessionId
          ? `${POSTHOG_HOST}/replay/${sessionId}`
          : null;

      const payload = {
        title: title.trim(),
        description: description.trim(),
        severity,
        url: window.location.href,
        userAgent: navigator.userAgent,
        locationId: selectedLocationId || null,
        consoleErrors: errorBuffer.slice(-50),
        posthogSessionId: sessionId,
        posthogDistinctId: distinctId,
        posthogReplayUrl: replayUrl,
      };
      const res = await apiRequest("POST", "/api/bug-reports", payload);
      const created = await res.json();

      try {
        posthog.capture("bug_report_submitted", {
          bug_report_id: created.id,
          title: payload.title,
          severity: payload.severity,
          page_url: payload.url,
          page_path: location,
          location_id: payload.locationId,
          console_error_count: payload.consoleErrors.length,
          replay_url: payload.posthogReplayUrl,
        });
      } catch {}

      return created;
    },
    onSuccess: () => {
      toast({ title: "Bug report sent", description: "Thanks — the team has been notified." });
      setTitle(""); setDescription(""); setSeverity("medium");
      setOpen(false);
    },
    onError: (err: any) => {
      toast({ title: "Could not send report", description: err?.message || "Please try again.", variant: "destructive" });
    },
  });

  if (!user) return null;

  const canSubmit = title.trim().length >= 3 && description.trim().length >= 10 && !submit.isPending;

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => {
          setOpen(true);
          if (!fired.current) {
            try { posthog.capture("bug_report_opened", { page_path: location }); } catch {}
            fired.current = true;
          }
        }}
        className="text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
        data-testid="button-open-bug-report"
        aria-label="Report a bug"
        title="Report a bug"
      >
        <Bug className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-bug-report">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-orange-500" /> Report a Bug
            </DialogTitle>
            <DialogDescription>
              Tell us what went wrong. We auto-capture the page, your last 50 errors, and your PostHog session replay so we can see exactly what happened.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bug-title">Title</Label>
              <Input
                id="bug-title"
                placeholder="e.g. Geogrid map shows blank"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                data-testid="input-bug-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-description">What happened?</Label>
              <Textarea
                id="bug-description"
                placeholder="Steps to reproduce, what you expected, what actually happened…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                maxLength={4000}
                data-testid="input-bug-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-severity">Severity</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger id="bug-severity" data-testid="select-bug-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low — minor annoyance</SelectItem>
                  <SelectItem value="medium">Medium — feature partially broken</SelectItem>
                  <SelectItem value="high">High — blocks my work</SelectItem>
                  <SelectItem value="critical">Critical — app is unusable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2 border">
              Auto-attached: current URL, browser info, last {errorBuffer.length} console errors, PostHog session replay link.
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="button-bug-cancel">Cancel</Button>
            <Button
              onClick={() => submit.mutate()}
              disabled={!canSubmit}
              data-testid="button-bug-submit"
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
